/**
 * Recent Scans Dashboard Component
 *
 * Displays recent scan history in a compact, visual format
 * suitable for dashboard views.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Clock,
  Loader2,
  Target,
  TrendingDown,
  TrendingUp,
  Minus,
  Eye,
  ChevronRight,
  Activity,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import RiskBadge from '@/components/report/RiskBadge';
import { useReports } from '@/hooks/useReports';
import { useActiveScans } from '@/hooks/useActiveScans';
import { getEffectiveRiskLevelWithFindings } from '@/lib/theme-config';
import { ScanModal } from './ScanModal';

interface RecentScansWidgetProps {
  limit?: number;
  /** Called when the user clicks "Run Your First Scan". If omitted, the widget manages its own ScanModal. */
  onRunScan?: () => void;
}

export function RecentScansWidget({ limit = 5, onRunScan }: RecentScansWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading } = useReports(limit, 0);
  const { activeScans } = useActiveScans();

  const handleRunScan = onRunScan ?? (() => setModalOpen(true));

  const activeScanIds = new Set(activeScans.map((s) => s.scanId));
  const allReports = data?.reports || [];

  // Reports whose status is still queued/running but aren't in the active queue
  // (e.g. MCP-initiated scans not yet reconciled, or the (5,0) cache lagging
  // behind the poller). Render these as spinner rows so they're never shown as
  // clickable completed entries while still in-flight.
  const pendingFromReports = allReports.filter(
    (r) => !activeScanIds.has(r.scanId) && (r.status === 'queued' || r.status === 'running')
  );

  // Completed/failed rows — exclude anything in-flight (active queue or pending above).
  const pendingReportIds = new Set(pendingFromReports.map((r) => r.scanId));
  const recentScans = allReports.filter(
    (r) => !activeScanIds.has(r.scanId) && !pendingReportIds.has(r.scanId)
  );

  const getRiskTrendIcon = (score: number) => {
    if (score >= 7) return <TrendingDown className="w-4 h-4 text-red-500" />;
    if (score >= 4) return <Minus className="w-4 h-4 text-yellow-500" />;
    return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Scans</CardTitle>
          <CardDescription>Loading your recent security scans...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-muted rounded-lg" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Recent Scans
            </CardTitle>
            <CardDescription>Your latest security scans</CardDescription>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/reports">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {recentScans.length === 0 && activeScans.length === 0 && pendingFromReports.length === 0 ? (
          <div className="text-center py-8">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No scans yet</p>
            <Button size="sm" onClick={handleRunScan}>
              Run Your First Scan
            </Button>
            {!onRunScan && <ScanModal open={modalOpen} onOpenChange={setModalOpen} />}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Active (in-progress) scans — from the active queue */}
            {activeScans.map((scan) => (
              <div
                key={`active-${scan.scanId}`}
                className="flex items-center gap-4 p-3 rounded-lg border border-emerald-800/40 bg-emerald-950/20"
              >
                <div className="flex-shrink-0">
                  <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium truncate text-muted-foreground">
                      {scan.target}
                    </span>
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      {scan.targetType}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <div className="flex-1 h-1.5 rounded-full bg-zinc-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500/60"
                        style={{
                          width: scan.status === 'running' ? '60%' : '20%',
                          transition: 'width 1s ease-in-out',
                          animation: 'pulse 2s ease-in-out infinite',
                        }}
                      />
                    </div>
                    <span className="text-xs text-emerald-400 whitespace-nowrap">
                      {scan.status === 'running' ? 'Scanning...' : 'Queued'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {/* In-progress scans from the reports list (e.g. MCP-initiated,
                not yet in the active queue, or cache lagging behind poller) */}
            {pendingFromReports.map((scan) => (
              <div
                key={`pending-report-${scan.scanId}`}
                className="flex items-center gap-4 p-3 rounded-lg border border-emerald-800/40 bg-emerald-950/20"
              >
                <div className="flex-shrink-0">
                  <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium truncate text-muted-foreground">
                      {scan.target}
                    </span>
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      {scan.targetType}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <div className="flex-1 h-1.5 rounded-full bg-zinc-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500/60"
                        style={{
                          width: scan.status === 'running' ? '60%' : '20%',
                          transition: 'width 1s ease-in-out',
                          animation: 'pulse 2s ease-in-out infinite',
                        }}
                      />
                    </div>
                    <span className="text-xs text-emerald-400 whitespace-nowrap">
                      {scan.status === 'running' ? 'Scanning...' : 'Queued'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {/* Completed scans */}
            {recentScans.map((scan) => {
              const isFailed = scan.status === 'failed';
              const overallScore = scan.scores?.overall || 0;
              return (
                <Link
                  key={scan.scanId}
                  to={`/report?scanId=${scan.scanId}`}
                  className="block group"
                >
                  <div className="flex items-center gap-4 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div className="flex-shrink-0">
                      {isFailed ? (
                        <XCircle className="w-4 h-4 text-zinc-400" />
                      ) : (
                        getRiskTrendIcon(overallScore)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium truncate">{scan.target}</span>
                        <Badge variant="outline" className="text-xs">
                          {scan.targetType}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(scan.scannedAt), { addSuffix: true })}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <RiskBadge
                        level={
                          isFailed ? 'failed' : getEffectiveRiskLevelWithFindings(scan.recommendation, scan.scores?.overall, scan.findingSummary)
                        }
                        size="sm"
                      />
                    </div>
                    <Eye className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
